import { useMemo } from 'react';
import { CourtPadTab } from './CourtPadTab';

export interface AmericanoSingleCourtPadProps {
  /** The active Americano/Mexicano tournament. */
  tournament: any;
  /** The single court whose pad is rendered. */
  court: any;
  /** Records a result from the court pad. */
  onRecordResult?: any;
  /** Submission phase for the match being sent — forwarded to <CourtPadTab>. */
  submitStatus?: { phase: 'idle' | 'sending' | 'done'; matchId: string | null };
  /** Persists in-progress points without finalising. */
  onLiveScore?: any;
  /** Called when a player name is clicked. */
  onPlayerClick?: any;
  /**
   * Adapts an Americano match shape into the slot-based shape CourtPadTab expects.
   * Monolith-only helper (still defined at module scope in legacy/App.tsx); the
   * call-site must pass it.
   */
  americanoMatchToSlots: (m: any) => any;
  /**
   * The live `Padel24Engine` wiring object (still defined in legacy/App.tsx).
   * Forwarded to <CourtPadTab>.
   */
  Padel24Engine: any;
  /**
   * Plays an end-sound by key — shared audio helper from the monolith (Web Audio).
   * NOTE: still defined at module scope in legacy/App.tsx; the call-site must pass it.
   */
  playEndSound: (soundType?: string) => void;
  /**
   * Live clock from the parent — forwarded to <CourtPadTab> so the pad's match
   * feed runs on the SAME clock that resolved this session onto the court.
   * Omitted → CourtPadTab ticks its own.
   */
  now?: any;
  /** Projected match numbers + club scoreboard — forwarded to <CourtPadTab>. */
  globalMatchNumber?: Record<string, number> | null;
  clubStandings?: { standings: any; leader: any } | null;
  /** Device kiosk mode: forwarded to <CourtPadTab> → <CourtPadCard> to drop size caps. */
  fill?: boolean;
}

// Én fysisk pad for Americano-familien — gjenbruker CourtPadCard (via CourtPadTab)
// med slot-adapterte kamper. Format-navnet (AMERICANO/MEXICANO osv.) vises automatisk.
/**
 * Single-court banepad for the Americano family — wraps <CourtPadTab onlyCourt> with
 * slot-adapted matches so the shared court pad renders one court. Extracted verbatim from
 * the monolith (legacy/App.tsx); markup/logic unchanged. Takes `americanoMatchToSlots`,
 * `Padel24Engine` and `playEndSound` as props (all still module-scoped in the monolith).
 */
export function AmericanoSingleCourtPad({
  tournament,
  court,
  onRecordResult,
  submitStatus,
  onLiveScore,
  onPlayerClick,
  now,
  globalMatchNumber = null,
  clubStandings = null,
  americanoMatchToSlots,
  Padel24Engine,
  playEndSound,
  fill = false,
}: AmericanoSingleCourtPadProps) {
  // deviation: monolith deps are [tournament] only (americanoMatchToSlots was a
  // stable module-scope fn); preserved verbatim.
  const adapted = useMemo(
    () => ({ ...tournament, matches: (tournament.matches || []).map(americanoMatchToSlots) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tournament],
  );
  return (
    <CourtPadTab
      tournament={adapted}
      now={now}
      globalMatchNumber={globalMatchNumber}
      clubStandings={clubStandings}
      onlyCourt={court}
      onRecordResult={onRecordResult}
      submitStatus={submitStatus}
      onLiveScore={onLiveScore}
      onPlayerClick={onPlayerClick}
      Padel24Engine={Padel24Engine}
      playEndSound={playEndSound}
      fill={fill}
    />
  );
}
